import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { adminApi, useAuth } from "@/context/AuthContext";
import PageHead from "@/components/PageHead";
import { Plus, LogOut, Eye, Edit3, Trash2, ExternalLink, Users, Mail } from "lucide-react";
import { toast } from "sonner";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

export default function AdminJournalList() {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState(null);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const { data } = await adminApi.get("/journal/admin/all");
      setPosts(data);
    } catch (e) {
      if (e?.response?.status === 401) {
        logout();
        navigate("/admin/login", { replace: true });
        return;
      }
      setError("Could not load posts.");
    }
  }, [logout, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const onDelete = async (post) => {
    if (
      !window.confirm(`Delete "${post.title}"? This cannot be undone.`)
    ) return;
    setBusyId(post.id);
    try {
      await adminApi.delete(`/journal/${post.id}`);
      setPosts((p) => (p || []).filter((x) => x.id !== post.id));
      toast.success("Post deleted");
    } catch (e) {
      toast.error("Delete failed");
    } finally {
      setBusyId(null);
    }
  };

  const onTogglePublish = async (post) => {
    setBusyId(post.id);
    try {
      const { data } = await adminApi.put(`/journal/${post.id}`, {
        published: !post.published,
      });
      setPosts((p) => (p || []).map((x) => (x.id === post.id ? data : x)));
      toast.success(data.published ? "Published" : "Moved to draft");
    } catch (e) {
      toast.error("Update failed");
    } finally {
      setBusyId(null);
    }
  };

  const onLogout = () => {
    logout();
    navigate("/admin/login", { replace: true });
  };

  const published = (posts || []).filter((p) => p.published).length;
  const drafts = (posts || []).filter((p) => !p.published).length;

  return (
    <div data-testid="admin-journal-list-page">
      <PageHead
        title="Blog Admin — Redkey Web Design"
        description="Manage blog entries."
      />

      {/* Header */}
      <section className="mx-auto max-w-[1360px] px-6 md:px-10 pt-16 md:pt-20 pb-8">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="eyebrow">
              § Admin · Signed in as {admin?.email}
            </p>
            <h1 className="font-display text-4xl md:text-6xl mt-4">
              Blog <em className="not-italic text-brand-red">manager.</em>
            </h1>
            <p className="mt-4 font-body text-ink-soft">
              {posts === null
                ? "Loading…"
                : `${published} published · ${drafts} draft${
                    drafts === 1 ? "" : "s"
                  }`}
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/admin/leads"
              data-testid="admin-nav-leads"
              className="inline-flex items-center gap-2 border hairline-strong px-5 py-3 hover:border-ink font-ui"
            >
              <Mail size={16} aria-hidden />
              Leads
            </Link>
            <Link
              to="/admin/subscribers"
              data-testid="admin-nav-subscribers"
              className="inline-flex items-center gap-2 border hairline-strong px-5 py-3 hover:border-ink font-ui"
            >
              <Users size={16} aria-hidden />
              Subscribers
            </Link>
            <Link
              to="/admin/journal/new"
              data-testid="admin-new-post"
              className="inline-flex items-center gap-2 bg-ink text-paper px-5 py-3 hover:bg-electric transition-colors font-ui"
            >
              <Plus size={16} aria-hidden />
              New post
            </Link>
            <button
              type="button"
              onClick={onLogout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 border hairline-strong px-5 py-3 hover:border-ink font-ui"
            >
              <LogOut size={16} aria-hidden />
              Log out
            </button>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="mx-auto max-w-[1360px] px-6 md:px-10 border-t hairline pt-8 pb-24">
        {error && (
          <div
            className="border hairline-strong bg-paper-alt p-6 mb-8"
            data-testid="admin-list-error"
          >
            <p className="font-body text-ink">{error}</p>
          </div>
        )}

        {posts === null && !error && (
          <p className="font-body text-ink-soft" data-testid="admin-list-loading">
            Loading posts…
          </p>
        )}

        {posts !== null && posts.length === 0 && (
          <div
            className="border hairline-strong bg-paper-alt p-8"
            data-testid="admin-list-empty"
          >
            <p className="eyebrow">§ Empty archive</p>
            <h2 className="font-display text-3xl mt-4">
              No entries yet — write the first one.
            </h2>
            <Link
              to="/admin/journal/new"
              className="mt-6 inline-flex items-center gap-2 bg-ink text-paper px-5 py-3 hover:bg-electric transition-colors font-ui"
            >
              <Plus size={16} aria-hidden />
              Create first post
            </Link>
          </div>
        )}

        {posts !== null && posts.length > 0 && (
          <ul className="divide-y divide-[hsl(var(--line-strong))] border-y border-[hsl(var(--line-strong))]">
            {posts.map((p) => (
              <li
                key={p.id}
                data-testid={`admin-post-row-${p.slug}`}
                className="py-5 grid grid-cols-1 md:grid-cols-12 gap-4 items-center"
              >
                <div className="md:col-span-6">
                  <div className="flex items-center gap-3">
                    <span
                      className={`inline-block font-ui text-[10px] tracking-widest uppercase px-2 py-0.5 border ${
                        p.published
                          ? "text-electric-ink border-electric-ink"
                          : "text-label border-[hsl(var(--line-strong))]"
                      }`}
                      data-testid={`admin-post-status-${p.slug}`}
                    >
                      {p.published ? "Published" : "Draft"}
                    </span>
                    <span className="font-ui text-xs text-label truncate">
                      /blog/{p.slug}
                    </span>
                  </div>
                  <h3 className="font-display text-xl md:text-2xl mt-2">
                    {p.title}
                  </h3>
                </div>
                <div className="md:col-span-3 font-ui text-xs text-label">
                  <p>Updated {formatDate(p.updated_at)}</p>
                  {p.published_at && (
                    <p className="mt-1">Published {formatDate(p.published_at)}</p>
                  )}
                </div>
                <div className="md:col-span-3 flex flex-wrap gap-2 md:justify-end">
                  {p.published && (
                    <Link
                      to={`/blog/${p.slug}`}
                      target="_blank"
                      rel="noopener"
                      className="inline-flex items-center gap-1 border hairline-strong px-3 py-2 text-xs font-ui hover:border-ink"
                      data-testid={`admin-post-view-${p.slug}`}
                    >
                      <ExternalLink size={12} /> View
                    </Link>
                  )}
                  <button
                    type="button"
                    onClick={() => onTogglePublish(p)}
                    disabled={busyId === p.id}
                    data-testid={`admin-post-toggle-${p.slug}`}
                    className="inline-flex items-center gap-1 border hairline-strong px-3 py-2 text-xs font-ui hover:border-ink disabled:opacity-60"
                  >
                    <Eye size={12} />
                    {p.published ? "Unpublish" : "Publish"}
                  </button>
                  <Link
                    to={`/admin/journal/edit/${p.id}`}
                    className="inline-flex items-center gap-1 border hairline-strong px-3 py-2 text-xs font-ui hover:border-ink"
                    data-testid={`admin-post-edit-${p.slug}`}
                  >
                    <Edit3 size={12} /> Edit
                  </Link>
                  <button
                    type="button"
                    onClick={() => onDelete(p)}
                    disabled={busyId === p.id}
                    data-testid={`admin-post-delete-${p.slug}`}
                    className="inline-flex items-center gap-1 border border-brand-red-ink/50 text-brand-red-ink px-3 py-2 text-xs font-ui hover:bg-brand-red-ink/5 disabled:opacity-60"
                  >
                    <Trash2 size={12} /> Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
